import { CheckCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import Modal from 'antd/lib/modal';
import Notification from 'antd/lib/notification';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { isAfter } from 'date-fns';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { compareWithLocale } from 'src/domain/compareWithLocale';
import { AbilityContext } from '../../../components/ability/can';
import { ExpandRowsCheckbox } from '../../../components/ExpandRowsCheckbox/ExpandRowsCheckbox';
import { LoadingIcon } from '../../../components/icons/LoadingIcon';
import { AuditTrailModal } from '../../../components/modals/AuditTrailModal/AuditTrailModal';
import { ReadinessTrackingNominationsTable } from '../../../components/nominations/readiness-tracking/ReadinessTrackingNominationsTable';
import { NominationStatusTag } from '../../../components/nominations/readiness-tracking/NominationStatusTag';
import { PageFooter } from '../../../components/PageFooter';
import { PageHeader } from '../../../components/PageHeader';
import { ProgramTag } from '../../../components/ProgramTag';
import { SearchInput } from '../../../components/searchInput/searchInput';
import { SidebarGrid } from '../../../components/Sidebar/SidebarGrid';
import { ActionList } from '../../../components/table/actionList/actionList';
import { getColumnSortOrder } from '../../../components/table/columnFormatHelpers';
import { CustomizableColumnType, CustomizableTable } from '../../../components/table/CustomizableTable';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { WithEmptyDataTable } from '../../../components/table/withEmptyDataTable';
import { useDocumentTitle } from '../../../components/useDocumentTitle';
import { ViewSwitcher } from '../../../components/viewSwitcher/ViewSwitcher';
import { AuditEntityType } from '../../../domain/audit-trail/interface';
import { DEFAULT_PAGINATION, PAGE_ACTION } from '../../../domain/commonConst';
import { getComputerFormattedTime } from '../../../domain/date/date';
import { IPaginationMeta } from '../../../domain/IPagination';
import { exportNominationReadinessTracks } from '../../../domain/nomination/readiness';
import { INominationReadinessTrack, NominationReadinessTrackStatus } from '../../../domain/nomination/readiness/interface';
import {
    useNominationReadinessTrackConfirmMutation,
    useNominationReadinessTrackDeleteMutation,
    useNominationReadinessTrackListQuery,
} from '../../../domain/nomination/readiness/queries';
import { toUsefulParams } from '../../toUsefulParams';
import { usePageLocation } from '../../usePageState';
import { ReadinessTrackingSidebar } from '../readinessTrackingSidebar/ReadinessTrackingSidebar';
import { useReadinessTrackingSidebar } from '../readinessTrackingSidebar/useReadinessTrackingSidebar';
import { siteViewInterface } from './siteViewInterface';

interface IPageState {
    companyId?: number;
    filter?: {};
    search?: string;
    pagination?: {
        pageSize: number;
        current: number;
    };
    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export const NominationReadinessTracking = () => {
    const [modal, modalContextHolder] = Modal.useModal();
    const [notification, notificationContextHolder] = Notification.useNotification();
    const navigation = useNavigate();
    const ability = useContext(AbilityContext);
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: IPageState = queryToState(location.search);
    const params = toUsefulParams(pageState);
    const { isFiltersSidebarEnabled, sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useReadinessTrackingSidebar();

    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [readinessTrackingRecord, setReadinessTrackingRecord] = useState<INominationReadinessTrack | null>(null);
    const [pageAction, setPageAction] = useState<PAGE_ACTION | null>(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    const deleteMutation = useNominationReadinessTrackDeleteMutation();
    const confirmMutation = useNominationReadinessTrackConfirmMutation();

    const listQueryParams = {
        ...(pageState?.sorter && { sorter: pageState?.sorter }),
        ...(pageState?.search && { search: pageState?.search }),
        ...(pageState?.filter && { filter: pageState.filter }),
        ...(pageState?.companyId && { companyId: pageState.companyId }),
        pagination: params?.pagination ?? DEFAULT_PAGINATION,
        include: 'program',
    };
    const listQuery = useNominationReadinessTrackListQuery(listQueryParams);
    const isLoading = listQuery.isLoading;

    const nominationReadinessTracks = listQuery.data?.data || [];
    const meta = listQuery.data?.meta || ({} as IPaginationMeta);

    if (listQuery.isError) {
        notification.error({ message: 'Error loading Readiness Tracking', description: listQuery.error?.message || 'Unknown error' });
    }

    const onViewChange = (view: string) => {
        const companyQuery = pageState.companyId ? `?companyId=${pageState.companyId}` : '';
        navigation(`${view}${companyQuery}`);
    };

    const onTableChange = (
        pagination: TablePaginationConfig,
        _filter: Record<string, FilterValue | null>,
        sorter: SorterResult<INominationReadinessTrack> | SorterResult<INominationReadinessTrack>[]
    ) => {
        const preparedParams = toUsefulParams({ pagination, sorter });
        setPageQuery({ ...pageState, ...preparedParams });
    };

    const showPageActionModal = (action: PAGE_ACTION, record: INominationReadinessTrack) => {
        setReadinessTrackingRecord(record);
        setPageAction(action);
    };

    const pageActionModalClose = () => {
        setReadinessTrackingRecord(null);
        setPageAction(null);
    };

    const confirmReadinessTrack = async (record: INominationReadinessTrack) => {
        try {
            if (confirmMutation.isLoading) return;

            await confirmMutation.mutateAsync(record.id);

            notification.success({ key: 'success', message: 'Nominations Readiness Confirmed!' });
        } catch (err) {
            notification.error({ key: 'error', message: 'Cannot confirm Nominations Readiness!' });
        }
    };

    const deleteReadinessTrack = async (trackId: string) => {
        try {
            if (deleteMutation.isLoading) return;

            await deleteMutation.mutateAsync(trackId);

            notification.success({ key: 'success', message: 'Readiness Tracking Request Cancelled!' });
        } catch (err) {
            notification.error({ key: 'error', message: 'Cannot cancel Readiness Tracking Request!' });
        }
    };

    const handleExpandAll = () => {
        const allKeys = nominationReadinessTracks.map(record => record.id);
        setExpandedRowKeys(allKeys);
    };

    const handleCollapseAll = () => {
        setExpandedRowKeys([]);
    };

    const handleExportCsv = async () => {
        try {
            setExportLoading(true);

            await exportNominationReadinessTracks(listQueryParams);
        } catch (err: any) {
            notification.error({
                key: 'export-error',
                message: err.message || 'Cannot export data!',
            });
        } finally {
            setExportLoading(false);
        }
    };

    const handleSearch = useCallback(
        (value: string) => {
            if (value === pageState.search) {
                setPageQuery({
                    ...pageState,
                    pagination: { ...pageState.pagination, current: DEFAULT_PAGINATION.current },
                });
                return;
            }

            setPageQuery({ ...pageState, search: value });
        },
        [pageState, setPageQuery]
    );

    const actions = (record: INominationReadinessTrack) => {
        const actions = [
            {
                title: 'Audit Trail',
                onClick: (record: INominationReadinessTrack) => () => showPageActionModal(PAGE_ACTION.AUDIT_TRAIL, record),
                icon: <HistoryOutlined />,
            },
        ];

        if (
            ![NominationReadinessTrackStatus.SCHEDULED, NominationReadinessTrackStatus.SKIPPED, NominationReadinessTrackStatus.CONFIRMED].includes(
                record.status as NominationReadinessTrackStatus
            )
        ) {
            actions.push({
                title: 'Confirm Readiness',
                icon: <LoadingIcon icon={<CheckCircleOutlined />} loading={confirmMutation.isLoading} />,
                onClick: (record: INominationReadinessTrack) => () => confirmReadinessTrack(record),
            });
        }

        if (isAfter(record.dateToSendEmail, new Date())) {
            actions.push({
                title: `Cancel Request`,
                icon: <LoadingIcon icon={<CloseCircleOutlined />} loading={deleteMutation.isLoading} />,
                onClick: (record: INominationReadinessTrack) => () => {
                    modal.confirm({
                        title: 'Are you sure you want to cancel this Readiness Tracking request?',
                        icon: <ExclamationCircleOutlined />,
                        content: 'This action cannot be undone.',
                        onOk: () => deleteReadinessTrack(record.id),
                        closeIcon: true,
                        closable: true,
                    });
                },
            });
        }

        return actions;
    };

    const nominationColumns: CustomizableColumnType<INominationReadinessTrack>[] = [
        {
            title: 'Company',
            key: 'company_name',
            width: 200,
            render: (value, record) => record.user?.company?.company_name || 'N/A',

            dataIndex: 'company_name',
            sorter: (a, b) => compareWithLocale(a.user?.company?.company_name || '', b.user?.company?.company_name || ''),
            ...getColumnSortOrder('company_name', pageState.sorter),
        },
        {
            title: 'User',
            key: 'user_fname',
            dataIndex: 'user_fname',
            width: 200,
            render: (value, record) => `${record.user?.user_fname} ${record.user?.user_lname}`,
            sorter: (a, b) => compareWithLocale(`${a.user?.user_fname} ${a.user?.user_lname}`, `${b.user?.user_fname} ${b.user?.user_lname}`),
            ...getColumnSortOrder('user_fname', pageState.sorter),
        },
        {
            title: 'Email',
            key: 'user_email',
            dataIndex: 'user_email',
            width: 250,
            render: (value, record) => record.user?.user_email || 'N/A',
            sorter: (a, b) => compareWithLocale(a.user?.user_email || '', b.user?.user_email || ''),
            ...getColumnSortOrder('user_email', pageState.sorter),
        },
        {
            title: 'Account Manager',
            key: 'account_manager',
            dataIndex: 'account_manager',
            width: 150,
            render: (value, record) => record.accountManager,
            sorter: (a, b) => compareWithLocale(a.accountManager || '', b.accountManager || ''),
            ...getColumnSortOrder('account_manager', pageState.sorter),
        },
        {
            title: 'Programs',
            key: 'programs',
            dataIndex: 'programs',
            width: 200,
            render: (value, record) => {
                if (record.programs && record.programs.length > 0) {
                    return record.programs.map(program => <ProgramTag key={program.program_id} programName={program.name} />);
                }

                return null;
            },
        },
        {
            title: 'Sent',
            key: 'date_to_send_email',
            dataIndex: 'date_to_send_email',
            width: 120,
            render: (value, record) => getComputerFormattedTime(record.dateToSendEmail),
            sorter: (a, b) => compareWithLocale(a.dateToSendEmail.valueOf(), b.dateToSendEmail.valueOf()),
            ...getColumnSortOrder('date_to_send_email', pageState.sorter),
        },
        {
            title: 'Opened',
            key: 'date_email_opened',
            dataIndex: 'date_email_opened',
            width: 120,
            render: (value, record) => getComputerFormattedTime(record.dateEmailOpened),
            sorter: (a, b) => compareWithLocale(a.dateEmailOpened?.valueOf() || 0, b.dateEmailOpened?.valueOf() || 0),
            ...getColumnSortOrder('date_email_opened', pageState.sorter),
        },
        {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            width: 95,
            render: (value, record) => <NominationStatusTag nominationReadinessTrack={record} />,
        },
        {
            title: 'Comment',
            key: 'comment',
            dataIndex: 'comment',
            width: 150,
            render: (value, record) => record.comment || '',
        },
        {
            key: 'action',
            sorter: false,
            width: 100,
            render: (text: string, record) => <ActionList actions={actions(record)} item={record} />,
        },
    ] as CustomizableColumnType<INominationReadinessTrack>[];

    useDocumentTitle('Nomination Readiness Tracking');

    // Reset expanded rows on pagination change
    useEffect(() => {
        setExpandedRowKeys([]);
    }, [pageState.pagination?.current, pageState.pagination?.pageSize]);

    return (
        <>
            <CustomizableTableContextProvider configKey="nominationReadinessConfirmation" columns={nominationColumns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    {isFiltersSidebarEnabled && (
                        <ReadinessTrackingSidebar
                            collapsed={sidebarCollapsed}
                            onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                            totalRecords={meta?.total}
                            onFilterChange={() => {}}
                            gridComponent={<SidebarGrid />}
                        />
                    )}

                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader
                            pageTitle="Readiness Tracking"
                            extra={[
                                <ViewSwitcher
                                    viewOptions={siteViewInterface(ability)}
                                    key="program-view-switcher"
                                    view={location.pathname}
                                    handleViewMode={onViewChange}
                                />,
                            ]}
                            actions={[
                                <SearchInput onSearch={handleSearch} defaultValue={pageState?.search} key="nomination-readiness-search" />,
                                <Button key="download-csv" size="large" onClick={handleExportCsv} icon={<DownloadOutlined />} loading={exportLoading}>
                                    Download CSV
                                </Button>,
                            ]}
                        />
                        <WithEmptyDataTable condition={isLoading}>
                            <CustomizableTable<INominationReadinessTrack>
                                showHiddenColumnsCount
                                size="small"
                                rowKey="id"
                                sticky={{ offsetHeader: 62 }}
                                dataSource={nominationReadinessTracks}
                                loading={isLoading}
                                onChange={onTableChange}
                                meta={meta}
                                title={() => (
                                    <ExpandRowsCheckbox
                                        totalRows={nominationReadinessTracks.length}
                                        expandedRowKeys={expandedRowKeys}
                                        handleExpandAll={handleExpandAll}
                                        handleCollapseAll={handleCollapseAll}
                                    />
                                )}
                                expandable={{
                                    expandedRowKeys: expandedRowKeys,
                                    onExpandedRowsChange: keys => setExpandedRowKeys(keys as React.Key[]),
                                    expandedRowRender: (record, index, indent, expanded) => (
                                        <ReadinessTrackingNominationsTable nominationReadinessTrack={record} isRowExpanded={expanded} />
                                    ),
                                }}
                            />
                        </WithEmptyDataTable>

                        <PageFooter />
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>

            {pageAction === PAGE_ACTION.AUDIT_TRAIL && readinessTrackingRecord && (
                <AuditTrailModal
                    onClose={pageActionModalClose}
                    entityId={readinessTrackingRecord.id}
                    entityType={AuditEntityType.NOMINATION_READINESS_TRACK}
                />
            )}

            {modalContextHolder}
            {notificationContextHolder}
        </>
    );
};
